import { queryOptions, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useApi } from '@/context';
import type { SubscriptionsService } from '@/services';

// Purchasing goes through the purchase-intents flow (ESCLOUD-332): this file
// has no direct subscribe mutation.

export const subscriptionKeys = {
    all: ['subscriptions'] as const,
    detail: (tenantId: string) => [...subscriptionKeys.all, tenantId] as const,
};

/**
 * The one declaration of a tenant's subscription query (ESCLOUD-283) — key,
 * fetcher and options together, because they are not separable.
 *
 * TanStack keeps a single cache entry per key and every observer stamps its own
 * options onto it as it renders, so query-level options belong to the key, not
 * to the caller: two hooks declaring this key with different `retry` left the
 * effective policy decided by whichever route happened to be mounted last.
 * Anything a caller could disagree about is therefore fixed here.
 *
 * `poll` is the single knob, and it is the one option safe to vary because
 * `refetchInterval` is per-observer rather than per-query: a polling reader
 * refreshes the shared entry for everyone without changing how any other
 * reader's query behaves.
 *
 * `retry: false` for every reader. A 404 means "this tenant has no
 * subscription row", which is an answer and not a failure — the guard routes on
 * it — and the two readers of a genuine transport failure both want it
 * immediately rather than one backoff later: the guard shows a retry surface,
 * and the activation page counts consecutive misses.
 */
export function subscriptionQueryOptions(
    subscriptions: SubscriptionsService,
    tenantId: string | null | undefined,
    { poll = false }: { poll?: number | false } = {}
) {
    return queryOptions({
        queryKey: subscriptionKeys.detail(tenantId!),
        queryFn: () => subscriptions.getSubscription(tenantId!),
        enabled: !!tenantId,
        retry: false,
        refetchInterval: poll,
    });
}

export function useSubscription(tenantId: string | null | undefined) {
    const api = useApi();

    return useQuery(subscriptionQueryOptions(api.subscriptions, tenantId));
}

// A 404 from this query is an ANSWER, not a failure — "this tenant has no
// subscription row" — and `retry: false` above is what makes it arrive
// promptly. The predicate that says so is `isProvenNoSubscription` in
// `lib/subscription.ts`, next to the status classification, because its three
// callers all mock this module in their specs. Every reader of this query
// should use it rather than testing the status code again.

/**
 * Whether the current tenant's subscription grants read-only access
 * (ESCLOUD-288). Drives disabled mutating affordances and the banner —
 * presentation only, the server refuses regardless. Reads the server-derived
 * `accessLevel`; while the subscription is unknown (loading, error, no tenant)
 * this answers false, because greying the UI out on an unproven state would
 * punish a network blip — the server-side refusal is the safety net.
 */
export function useIsReadOnly(tenantId: string | null | undefined): boolean {
    const { data: subscription } = useSubscription(tenantId);
    return subscription?.accessLevel === 'read_only';
}

/**
 * The activation poll's schedule, and the whole of it: how often to ask, and
 * when to stop asking. `false` stops the poll.
 *
 * The bound exists because the tab can outlive the person watching it. A
 * post-checkout tab left open used to refetch a regional API every 2.5s for as
 * long as the browser stayed open — 1440 requests an hour, per abandoned tab,
 * for an answer nobody was reading.
 *
 * Stopping is safe, and that is the point: the webhook activates the
 * subscription and the nightly reconciliation heals a webhook that never
 * arrived. Neither one needs this page open. So the schedule is tuned for the
 * person who IS watching — the normal case resolves inside the first phase —
 * and the later phases only exist so that a slow day is still covered before
 * the page admits it has stopped looking.
 */
const ACTIVATION_POLL = {
    /** The webhook normally lands in seconds; this should feel immediate. */
    fast: 2_500,
    /** Past the point where a watcher has been told the wait is unusual. */
    backoff: 10_000,
    /**
     * Five minutes. Longer than any activation this page will ever see resolve,
     * and long enough that a Stripe webhook retry has had its chance; past it,
     * more polling buys nothing that reconciliation does not already cover.
     */
    stopped: false,
} as const;

/** ms after mount at which each phase gives way to the next. */
const ACTIVATION_POLL_BACKOFF_AFTER_MS = 30_000;
const ACTIVATION_POLL_STOPS_AFTER_MS = 5 * 60_000;

/** Where the activation poll has got to. Also drives what the page says. */
export type TActivationPollPhase = keyof typeof ACTIVATION_POLL;

/**
 * The phases are wall-clock from mount rather than a count of polls, so a
 * stretch of failed requests cannot extend the bound: the point is to stop
 * asking, and a poll that keeps failing is the case that most needs to.
 */
function useActivationPollPhase(): TActivationPollPhase {
    const [phase, setPhase] = useState<TActivationPollPhase>('fast');

    useEffect(() => {
        const toBackoff = setTimeout(() => setPhase('backoff'), ACTIVATION_POLL_BACKOFF_AFTER_MS);
        const toStopped = setTimeout(() => setPhase('stopped'), ACTIVATION_POLL_STOPS_AFTER_MS);
        return () => {
            clearTimeout(toBackoff);
            clearTimeout(toStopped);
        };
    }, []);

    return phase;
}

/**
 * The subscription query, polled (ESCLOUD-283): the "activating…" page waits
 * for the webhook to write the row, so it refetches on an interval and treats
 * a 404 (the row does not exist yet) as "not yet", never as failure. Shares
 * the guard's query key, so the moment activation is seen the whole app knows.
 *
 * Errors are NOT swallowed: `error` and `errorUpdatedAt` are what let the page
 * tell a pending webhook apart from polls that never reach the server, so a
 * caller reading only `data` would silently present the second as the first.
 *
 * The poll backs off and then stops — see `ACTIVATION_POLL`. `pollPhase` is
 * returned because the page has to say so: a spinner over a poll that has
 * stopped would be the one dishonest state this page is careful never to show.
 */
export function useSubscriptionActivation(tenantId: string | null | undefined) {
    const api = useApi();
    const pollPhase = useActivationPollPhase();

    const query = useQuery(
        subscriptionQueryOptions(api.subscriptions, tenantId, { poll: ACTIVATION_POLL[pollPhase] })
    );

    return { ...query, pollPhase };
}
